import ForumIcon from "@mui/icons-material/Forum";
import { Alert, Box, IconButton, Paper, Snackbar, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { ObjectTarget } from "../../app/permissions";
import { ClassroomInfo } from "../../redux/classroom/classroomSlice";
import { LoadState } from "../../redux/common";
import { selectConversationList } from "../../redux/conversation/conversationListSlice";
import { ConversationPartyInfo } from "../../redux/conversation/conversationSlice";
import { DptoInfo } from "../../redux/dpto/dptoSlice";
import { SchoolInfo } from "../../redux/school/schoolSlice";
import ElementSkeleton from "../base/ElementSkeleton";
import FragmentFullContent from "../base/FragmentFullContent";
import { ConversationTile } from "./ConversationTile";
import MassiveMessageModal from "./MassiveMessageModal";
import PollIcon from '@mui/icons-material/Poll';
import SurveyModal from "./SurveyModal";

export interface ConversationContact {
    name: string;
    message: string;
    dt: string;
}

interface ConversationListProps {
    height: number;
    // me: NamedObject;
    clickAddContact: () => void;
    school?: SchoolInfo;
    classroom?: ClassroomInfo;
    dpto?: DptoInfo;
}

export const ConversationList = (props: ConversationListProps) => {
    const conversationList = useSelector(selectConversationList);
    const messageSendMassive = useSelector((state: any) => state.messageSendMassive);
    const [filterContact, setFilterContact] = useState("");
    const [massiveModalOpen, setMassiveModalOpen] = useState(false);
    const [surveyModalOpen, setSurveyModalOpen] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    if (!conversationList.list) {
        return <ElementSkeleton />;
    }

    const topHeight = 96;
    const messageHeight = props.height - topHeight;

    const contacts = () => {
        if (conversationList.list?.length == 0) {
            return <FragmentFullContent>
                <Paper elevation={3}
                    sx={{
                        width: "100%",
                        padding: 5, margin: 5,
                        borderRadius: 0
                    }}>
                    <Typography variant="h6">Nenhum contato</Typography>
                </Paper>
            </FragmentFullContent>;
        }

        return (conversationList.list || [])
            .filter((conversation) => {
                if (props.classroom) {
                    return conversation.parties.some((party: ConversationPartyInfo) => {
                        return party.objectTarget == ObjectTarget.CLASSROOM
                            && party.targetId == props.classroom?.id;
                    });
                } else if (props.dpto) {
                    return conversation.parties.some((party: ConversationPartyInfo) => {
                        return party.objectTarget == ObjectTarget.DPTO
                            && party.targetId == props.dpto?.id;
                    });
                } else if (props.school) {
                    return conversation.parties.some((party: ConversationPartyInfo) => {
                        return party.objectTarget == ObjectTarget.SCHOOL
                            && party.targetId == props.school?.id;
                    });
                } else {
                    return true;
                }
            })
            .filter((conversation) => {
                // TODO consider the acentuation
                return conversation.id.toLowerCase().includes(filterContact.toLowerCase());
            })
            .map((conversation, index) => {
                return <ConversationTile key={index} conversation={conversation} />;
            });
    };

    const filerContacts = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFilterContact(e.target.value);
    };

    const target = props.dpto ? ObjectTarget.DPTO :
        props.classroom ? ObjectTarget.CLASSROOM :
            ObjectTarget.SCHOOL;
    const targetId = props.dpto ? props.dpto.id :
        props.classroom ? props.classroom.id :
            props.school ? props.school.id :
                undefined;
    const schoolId = props.dpto ? props.dpto.schoolId :
        props.classroom ? props.classroom.schoolId :
            props.school ? props.school.id : 
                undefined;
    const objectName = props.dpto ? "departamento" :
        props.classroom ? "sala de aula" :
            props.school ? "escola" :
                "*";
    return <>
        <Paper elevation={3} sx={{ height: topHeight, borderRadius: 0, borderLeft: "1px black solid", backgroundColor: "primary.main" }}>
            <Stack direction="row" spacing={0}>
                <Stack direction="column" spacing={1} sx={{ paddingY: 1, paddingX: 1, width: "100%" }}>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="h6" color="primary.contrastText">Contatos</Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        {
                            props.school &&
                            <Tooltip title="Enviar mensagem para todos os contatos" >
                                <IconButton
                                    color='inherit'
                                    disabled={messageSendMassive.state.state === LoadState.Loading}
                                    onClick={() => setMassiveModalOpen(true)}
                                    sx={{ width: "30px", height: "30px", borderBottom: "1px solid gray", borderRadius: "0", color: "primary.contrastText" }} size='small'>
                                    <ForumIcon />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            props.school &&
                            <Tooltip title="Enquete" >
                                <IconButton
                                    color='inherit'
                                    disabled={messageSendMassive.state.state === LoadState.Loading}
                                    onClick={() => setSurveyModalOpen(true)}
                                    sx={{ width: "30px", height: "30px", borderBottom: "1px solid gray", borderRadius: "0", color: "primary.contrastText" }} size='small'>
                                    <PollIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <TextField size='small' id="outlined-basic" label="Pesquisar" variant="outlined" fullWidth
                            sx={{
                                input: { color: "primary.contrastText" }, 
                                label: { color: "primary.contrastText" }, 
                                fieldset: { borderColor: "primary.contrastText" }, 
                            }}
                            onChange={(e) => filerContacts(e)} />
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
        <Box sx={{ padding: 0, border: "1px gray solid", width: "100%", height: messageHeight, overflowY: "scroll" }}>
            {contacts()}
        </Box>
        <MassiveMessageModal
            open={massiveModalOpen}
            onClose={(success: boolean) => {
                if (success) {
                    setShowSuccess(true);
                }
                setMassiveModalOpen(false);
            }}
            target={target} targetId={targetId} objectName={objectName}
        />
        <SurveyModal
            open={surveyModalOpen}
            onClose={(success: boolean) => {
                if (success) {
                    setShowSuccess(true);
                }
                setSurveyModalOpen(false);
            }}
            target={target} targetId={targetId} objectName={objectName}
            schoolId={`${schoolId}`}
        />
        <Snackbar open={showSuccess} autoHideDuration={5000} onClose={() => setShowSuccess(false)}>
            <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: "100%" }}>
                Mensagem em massa enviada.
            </Alert>
        </Snackbar>

    </>;

};